import { P2GBox } from '@parcel2go/core-ui';
import { P2GDivider } from '@parcel2go/core-ui';
import { P2GDropdown } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { P2GLink } from '@parcel2go/core-ui';
import { P2GLinkGroup } from '@parcel2go/core-ui';

import { createPageLinkOptions } from '../../lib/helpers/PaginationHelper';
import { GenerateNumberOptions } from '../../lib/helpers/ArrayHelper';
import { Fragment } from 'react/jsx-runtime';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';

interface props {
  isXs: boolean;
  itemCount: number;
  skip: number;
  take: number;
  setPaging: (skip: number, take: number) => void;
  children: React.ReactNode;
}

export const getPageNumber = (skip: number, take: number) => Math.ceil(skip / take);
export const getSkipFromPageNumber = (page: number, take: number) => take * (page - 1);

export const P2GPagination: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const pageSizeOptions = [5, 10, 25, 50, 75, 100];
  const setPaging = (page: number) => props.setPaging(getSkipFromPageNumber(page, props.take), props.take);
  const setPageSize = (page: number) => props.setPaging(0, page);
  const totalPages = Math.ceil(props.itemCount / props.take);
  const currentPageNumber = getPageNumber(props.skip, props.take) + 1;

  const renderPageLinks = (currentPage: number, totalPages: number) =>
    createPageLinkOptions(currentPage, totalPages).map((page, i) => {
      return page.type == 'break' ? (
        <Fragment key={`break-${i}`}>...</Fragment>
      ) : (
        <P2GLink
          key={`${page.value}_${i}`}
          underline={page.selected ? 'none' : 'always'}
          onClick={() => setPaging(page.value || 0)}
        >
          {page.selected ? <strong>{page.value}</strong> : page.value}
        </P2GLink>
      );
    });

  return (
    <>
      <P2GBox pb={2}>{props.children}</P2GBox>
      {(totalPages > 1 || props.itemCount > pageSizeOptions[0]) && (
        <P2GBox pb={2}>
          <P2GBox pb={3}>
            <P2GDivider />
          </P2GBox>
          <P2GGrid container spacing={2} alignItems="center">
            <P2GGrid item xs={6} sm={2} flexShrink={1}>
              <P2GDropdown
                options={pageSizeOptions.map((x) => ({ value: x.toString() }))}
                value={String(props.take)}
                id="Items per page"
                label={t('PAGINATION_ITEMS_PER_PAGE_LABEL', { defaultValue: 'Items per page' })}
                onChange={(v) => setPageSize(Number(v))}
              />
            </P2GGrid>
            {totalPages > 1 && totalPages < 500 && (
              <P2GGrid item xs={6} sm={2}>
                <P2GDropdown
                  onChange={(v) => setPaging(Number(v))}
                  id="Page"
                  label={t('PAGINATION_PAGE_NUMBER_LABEL', { defaultValue: 'Page' })}
                  value={String(currentPageNumber)}
                  options={GenerateNumberOptions(1, totalPages)}
                />
              </P2GGrid>
            )}
            {!props.isXs && (
              <>
                <P2GGrid item flexGrow={1}></P2GGrid>
                <P2GGrid item flexShrink={1}>
                  <P2GLinkGroup links={renderPageLinks(currentPageNumber, totalPages)} />
                </P2GGrid>
              </>
            )}
          </P2GGrid>
        </P2GBox>
      )}
    </>
  );
};
